/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * FAQPage JSON-LD for the site-wide `faq.q*` / `faq.a*` translation pairs
 * (the homepage accordion). Plain ESM JavaScript on purpose — the
 * deepMerge.js / articleSeo.js pattern — because it has consumers that
 * cannot share TypeScript:
 *   1. src/hooks/useFAQSchema.ts — the client hook (source of truth at
 *      runtime: it replaces the baked script in place after hydration)
 *   2. the head of every page (src/lib/seoHead.js through
 *      src/lib/routeHeadSchema.js) — the bake and the request-time head
 *
 * Both call the same builder, so the baked payload and the hydrated payload
 * are byte-identical and the client replacement is a no-op.
 */

/** Script id shared by the bake and the client hook (found-by-id, replaced in place). */
export const FAQ_JSONLD_ID = 'jsonld-faqpage';

/**
 * @typedef {{question: string, answer: string}} FaqEntry
 * @typedef {{'@type': 'Question', name: string, acceptedAnswer: {'@type': 'Answer', text: string}}} FaqQuestionNode
 * @typedef {{'@context': 'https://schema.org', '@type': 'FAQPage', inLanguage: 'nl-NL'|'en-US', mainEntity: FaqQuestionNode[]}} FaqPageJsonLd
 */

/**
 * Markdown links `[label](url)` collapse to their label and whitespace is
 * normalised; HTML is deliberately left alone (Google allows limited HTML in
 * an Answer's text, and the homepage answers come from the WYSIWYG editor).
 * @param {unknown} s
 * @returns {string}
 */
const cleanFaqText = (s) =>
  String(s ?? '')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();

/**
 * Extract ordered Q&A pairs from the `faq` translation object: every `q<N>`
 * with a non-empty string `a<N>` twin, in object key order. Pairs the CMS left
 * incomplete are skipped.
 * @param {unknown} faq — the merged `faq` section of a language's translations
 * @returns {FaqEntry[]}
 */
export function globalFaqEntries(faq) {
  /** @type {FaqEntry[]} */
  const entries = [];
  if (!faq || typeof faq !== 'object') return entries;
  for (const key of Object.keys(faq)) {
    if (!key.startsWith('q')) continue;
    const num = key.slice(1);
    const question = /** @type {any} */ (faq)[key];
    const answer = /** @type {any} */ (faq)[`a${num}`];
    if (typeof question === 'string' && typeof answer === 'string' && question && answer) {
      entries.push({ question, answer });
    }
  }
  return entries;
}

/**
 * Build the FAQPage payload. Returns null when there is nothing to emit so the
 * caller never writes an empty FAQPage.
 * @param {FaqEntry[]} entries
 * @param {'nl'|'en'} lang
 * @returns {FaqPageJsonLd|null}
 */
export function buildFAQPageJsonLd(entries, lang) {
  /** @type {FaqQuestionNode[]} */
  const mainEntity = [];
  for (const entry of entries ?? []) {
    const question = cleanFaqText(entry?.question);
    const answer = cleanFaqText(entry?.answer);
    if (!question || !answer) continue;
    mainEntity.push({
      '@type': 'Question',
      name: question,
      acceptedAnswer: { '@type': 'Answer', text: answer },
    });
  }
  if (mainEntity.length === 0) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    inLanguage: lang === 'nl' ? 'nl-NL' : 'en-US',
    mainEntity,
  };
}
